import { Stack } from "@mui/material";
import { type CardProps } from "@mui/material/Card";
import Skeleton from "@mui/material/Skeleton";
import { PensionPhaseStatus, ProductType } from "@repo/api";
import { BodyTextM, cs, Overview } from "@repo/ui";
import { type FC, type ReactElement } from "react";
import { useContractId } from "../../context/ContractIDContext";
import { NumberToCZK } from "../../functions";
import {
	useContractById,
	usePensionDetail,
} from "../../queryHooks";
import { useAllowedChanges } from "@repo/logic";

interface ProductOverviewProps extends CardProps {
	image: string;
	title?: string;
	icon?: ReactElement;
	totalSavings?: number;
	claimedSavings?: number;
	isFundsChangeInProgress?: boolean;
}

const renderSavedAmount = (
	isChangesNotAllowed: boolean,
	isDataLoaded: boolean,
	savedAmount: number
): JSX.Element => {
	switch (true) {
		case isChangesNotAllowed:
			return <>{cs.global.changeInProgress}</>;
		case isDataLoaded:
			return <>{NumberToCZK(savedAmount)}</>;
		default:
			return (
				<>
					<Skeleton width={70} sx={{ display: "inline-block" }} /> Kč
				</>
			);
	}
}

const ProductOverview: FC<ProductOverviewProps> = ({
	title,
	image,
	icon,
	sx,
	...rest
}) => {
	const { contractId: id } = useContractId();
	const { data: contractData, isLoading: contractDataLoading } =
		useContractById({
			id: id,
			options: { refetchOnMount: false },
		});

	const { data: starpensionData, isLoading: starpensionDataLoading } =
		usePensionDetail({
			id,
			options: {
				enabled:
					contractData?.type !== undefined &&
					(contractData.type === ProductType.Uf || contractData.type === ProductType.Pf) &&
					!!id,
			},
		});

	const { data: allowedChangesData } = useAllowedChanges({
		id,
		options: {
			enabled:
				contractData?.type !== undefined &&
				contractData.type === ProductType.Uf &&
				!!id,
		},
	});

	if (contractDataLoading || contractData === undefined)
		return (
			<Overview {...rest} image={image} sx={sx} alt={title}>
				<Stack
					direction="row"
					sx={{ flexGrow: { md: 1 }, alignItems: "center" }}
				>
					{icon}
					<Stack sx={{ flexGrow: 1 }}>
						<Stack direction="row" sx={{ justifyContent: "space-between" }}>
							<BodyTextM>Mám naspořeno</BodyTextM>
							<>
								<Skeleton width={70} sx={{ display: "inline-block" }} /> Kč
							</>
						</Stack>
					</Stack>
				</Stack>
			</Overview>
		);

	const isChangesNotAllowed =
		allowedChangesData !== undefined &&
		!allowedChangesData.canChangeInvestmentStrategy;
	const isDataLoaded = starpensionData !== undefined && !starpensionDataLoading;
	const savedAmount =
		starpensionData !== undefined ? starpensionData.savedAmount : 0;

	switch (contractData?.pensionPhaseStatus) {
		case PensionPhaseStatus.Saving: {
			return (
				<Overview {...rest} image={image} sx={sx} alt={title}>
					<Stack
						direction="row"
						sx={{ flexGrow: { md: 1 }, alignItems: "center" }}
					>
						{icon}
						<Stack sx={{ flexGrow: 1 }}>
							<Stack direction="row" sx={{ justifyContent: "space-between" }}>
								<BodyTextM>Mám naspořeno</BodyTextM>
								{renderSavedAmount(
									isChangesNotAllowed,
									isDataLoaded,
									savedAmount
								)}
							</Stack>
						</Stack>
					</Stack>
				</Overview>
			);
		}
		case PensionPhaseStatus.Payout: {
			return (
				<Overview {...rest} image={image} sx={sx} alt={title}>
					<Stack
						sx={{
							justifyContent: "center",
							alignItems: "center",
							width: "100%",
						}}
					>
						<BodyTextM sx={{ textAlign: "center" }}>
							{cs.global.terminatedContract}
						</BodyTextM>
					</Stack>
				</Overview>
			);
		}
		default:
			throw new Error(
				"Invalid pension phase status: " + contractData?.pensionPhaseStatus
			);
	}
}
export default ProductOverview;
